/**
 * ServerListToolbar (Plans 6.2) — server-driven list controls.
 *
 * Unlike DataTableToolbar (client-side TanStack column filters), this drives
 * the server query state directly: search + faceted filters are forwarded as
 * query params, and the query key therefore contains the full list state.
 */
import { Cross2Icon } from "@radix-ui/react-icons";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTableFacetedFilter } from "@/components/data-table/faceted-filter";
import { DataTableViewOptions } from "@/components/data-table/view-options";
import type { Table } from "@tanstack/react-table";

export type ServerFilterOption = {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
};

type ServerListToolbarProps<TData> = {
  table: Table<TData>;
  searchPlaceholder?: string;
  /**
   * Optional: lists whose backend has no search param (e.g. moderation
   * reports/comments, guestbook) omit these two; the search input is then
   * not rendered at all instead of showing a dead box.
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filters?: {
    key: string;
    title: string;
    options: ServerFilterOption[];
    value?: string[];
    onChange: (values: string[]) => void;
  }[];
  onReset: () => void;
  viewButtonLabel?: string;
};

export function ServerListToolbar<TData>({
  table,
  searchPlaceholder = "Tìm nhanh...",
  searchValue,
  onSearchChange,
  filters = [],
  onReset,
  viewButtonLabel,
}: ServerListToolbarProps<TData>) {
  const hasSearch = typeof searchValue === "string" && typeof onSearchChange === "function";
  const isFiltered = (hasSearch && Boolean(searchValue)) || filters.some((f) => (f.value?.length ?? 0) > 0);

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
        {hasSearch ? (
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            className="h-8 w-full sm:w-[220px] lg:w-[280px]"
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((filter) => (
            <DataTableFacetedFilter
              key={filter.key}
              column={{
                getFilterValue: () => filter.value ?? [],
                setFilterValue: (v: unknown) => filter.onChange((v as string[]) ?? []),
                id: filter.key,
                getFacetedUniqueValues: () => new Map<string, number>(),
              } as never}
              title={filter.title}
              options={filter.options}
            />
          ))}
          {isFiltered ? (
            <Button variant="ghost" onClick={onReset} className="h-8 px-2 lg:px-3">
              Đặt lại
              <Cross2Icon className="ms-2 size-4" />
            </Button>
          ) : null}
        </div>
      </div>
      <DataTableViewOptions table={table} label={viewButtonLabel} />
    </div>
  );
}